import { useLocation, useSearchParams } from 'react-router-dom';
import { PRESEASON_WEEK } from '@ff/core';
import { useCurrentSeason } from '../lib/currentSeason.tsx';
import { TrashTalk } from '../components/TrashTalk.tsx';
import { DraftScheduler } from '../components/DraftScheduler.tsx';
import { DraftBoard } from '../components/DraftBoard.tsx';

/**
 * Draft Results: everything that happened before a snap was played.
 *
 * It starts as the draft-date poll and a trash-talk board, and becomes the
 * draft board the moment there are picks to show — the reference for "who took
 * him and in what round", which is worth having all season rather than for the
 * fortnight before the draft.
 *
 * The trash talk still closes when week 1 kicks off, and stays below as the
 * archive of what everyone said before they knew anything.
 */
export function DraftResults() {
  const { data, seasonStarted, ownerName } = useCurrentSeason();
  const season = data?.bundle.data.season ?? 0;
  const picks = data?.bundle.draft ?? [];
  const drafted = picks.length > 0;
  const nameOf = (id: string) => ownerName.get(id) ?? 'Unknown';
  // A tapped notification names its post; see Scoreboard for why the key rides along.
  const [params] = useSearchParams();
  const focusPostId = params.get('post');
  const { key: focusKey } = useLocation();

  return (
    <div className="space-y-6">
      <div>
        <p className="eyebrow">
          {season} · {drafted ? 'the draft board' : 'before a single snap'}
        </p>
        <h1 className="mt-1 font-display text-3xl font-black uppercase tracking-tight">
          Draft Results
        </h1>
        {drafted ? (
          <p className="mt-2 max-w-prose font-editorial text-sm text-chalk-dim">
            Every pick, in order. Round one is where the $25 Clown comes from —
            the rest is for settling arguments about who reached for whom.
          </p>
        ) : (
          <p className="mt-2 max-w-prose font-editorial text-sm text-chalk-dim">
            No games on the board yet — just talk. Call your shots and settle it
            at the draft.
          </p>
        )}
      </div>

      <hr className="border-felt-line" />

      {/* Once the picks are in, the date poll has nothing left to say. */}
      {drafted ? (
        <DraftBoard picks={picks} nameOf={nameOf} />
      ) : (
        <DraftScheduler season={season} readOnly={seasonStarted} />
      )}

      <hr className="border-felt-line" />

      <TrashTalk
        season={season}
        week={PRESEASON_WEEK}
        readOnly={seasonStarted}
        focusPostId={focusPostId}
        focusKey={focusKey}
        subtitle={seasonStarted ? 'Before the draft · archived' : 'Before the draft'}
        emptyPrompt={
          seasonStarted
            ? 'Nobody said a word before this draft.'
            : "Draft's coming. Fire the first shot."
        }
      />
    </div>
  );
}
